/**
 * AdminRecetasV31.jsx · v3.1.0 · Feb-2026
 * ────────────────────────────────────────────────────────────────
 * Editor de Recetas por Categoría + Schema de Fiscalización dinámico
 *
 * 3 columnas:
 *   1. Lista de Tipos de Solución
 *   2. Receta (líneas mixtas: producto exacto | categoría Odoo)
 *   3. Schema de Fiscalización (JSON de campos dinámicos)
 */
import { useState, useEffect, useMemo } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import CategoryTreePicker from "../components/CategoryTreePicker";
import FiscFormLinkModal from "../components/FiscFormLinkModal";
import {
  Loader2, Plus, Trash2, Package, FolderTree, Save, Search,
  ChevronRight, AlertCircle, CheckCircle2, X, ClipboardList, Link2, ExternalLink,
} from "lucide-react";

const FIELD_TYPES = ["text", "number", "mac", "boolean", "select"];

export default function AdminRecetasV31() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [q, setQ] = useState("");
  const [formModal, setFormModal] = useState(null); // v3.9.4 · st seleccionado para modal formulario

  const loadTypes = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v1/mesa/solution-types/search", { params: { q: "" } });
      setTypes(data.items || []);
    } catch (e) { toast.error("Error cargando tipos"); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadTypes(); }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return types;
    return types.filter((x) => (x.code || "").toLowerCase().includes(t) || (x.name || "").toLowerCase().includes(t));
  }, [q, types]);

  return (
    <Layout>
      <div className="p-4 max-w-[1600px] mx-auto">
        <div className="mb-4">
          <h1 className="text-xl font-bold" style={{ color: "#0A3747" }}>
            Recetas v3.1.0 · Categorías + Fiscalización dinámica
          </h1>
          <p className="text-[11px] text-gray-500">
            Configura por tipo de solución: líneas mixtas de receta (producto exacto o categoría) + esquema de campos que el técnico debe llenar al fiscalizar.
          </p>
        </div>

        <div className="grid grid-cols-12 gap-3">
          {/* Columna 1 · Lista de solution_types */}
          <aside className="col-span-3 bg-white border rounded-lg overflow-hidden">
            <div className="p-2 border-b bg-slate-50">
              <div className="relative">
                <Search size={12} className="absolute left-2 top-2 text-gray-400"/>
                <input
                  value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar…"
                  className="w-full pl-6 pr-2 py-1 text-xs border rounded"
                  data-testid="recetas-search"
                />
              </div>
            </div>
            <div className="max-h-[70vh] overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center"><Loader2 size={16} className="animate-spin text-gray-400"/></div>
              ) : filtered.length === 0 ? (
                <div className="p-4 text-[11px] text-gray-400">Sin resultados</div>
              ) : (
                filtered.map((st) => (
                  <div
                    key={st.id}
                    className={`w-full border-b flex items-stretch ${selectedId === st.id ? "bg-blue-100 border-l-2 border-l-blue-600" : "hover:bg-blue-50"}`}
                    data-testid={`recetas-item-${st.code}`}
                  >
                    <button
                      onClick={() => setSelectedId(st.id)}
                      className="flex-1 text-left px-2 py-1.5 flex items-center gap-1.5"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-bold truncate">{st.name}</div>
                        <div className="text-[9px] font-mono text-gray-500">{st.code} · mat: {st.materials_count || 0}</div>
                      </div>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setFormModal(st); }}
                      className="px-1.5 border-l border-gray-200 bg-blue-50 text-blue-700 hover:bg-blue-100 flex items-center gap-0.5 text-[9px] font-bold"
                      data-testid={`recetas-item-form-btn-${st.code}`}
                      title="Configurar formulario de fiscalización"
                    >
                      📋
                    </button>
                    <ChevronRight size={11} className="text-gray-400 self-center mr-1"/>
                  </div>
                ))
              )}
            </div>
          </aside>

          {/* Columnas 2+3 · editor */}
          <main className="col-span-9">
            {!selectedId ? (
              <div className="bg-white border rounded-lg p-16 text-center text-gray-400" data-testid="recetas-empty">
                <FolderTree size={40} className="mx-auto mb-3 opacity-40"/>
                <div className="text-[13px] font-semibold">Selecciona un Tipo de Solución</div>
              </div>
            ) : (
              <TypeEditor stId={selectedId} onReload={loadTypes}/>
            )}
          </main>
        </div>
      </div>

      {/* v3.9.4 · Modal: vincular formulario de fiscalización */}
      {formModal && (
        <FiscFormLinkModal st={formModal} onClose={() => setFormModal(null)} />
      )}
    </Layout>
  );
}


// ═══════════════════════════════════════════════════════════════════
function TypeEditor({ stId, onReload }) {
  const [full, setFull] = useState(null);
  const [schema, setSchema] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/v1/mesa/solution-types/${stId}/full`);
      setFull(data);
      const { data: s } = await api.get(`/v1/mesa/solution-types/${stId}/schema`);
      setSchema(s.fiscalizacion_schema || []);
    } catch (e) { toast.error("Error cargando tipo"); }
  };
  useEffect(() => { load(); }, [stId]);

  const saveSchema = async () => {
    setSaving(true);
    try {
      await api.put(`/v1/mesa/solution-types/${stId}/schema`, { fiscalizacion_schema: schema });
      toast.success("Schema de fiscalización guardado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando schema");
    } finally { setSaving(false); }
  };

  if (!full) return <div className="p-8 text-center"><Loader2 className="animate-spin text-gray-400 mx-auto"/></div>;

  return (
    <div className="grid grid-cols-2 gap-3">
      <RecipeEditor full={full} onReload={() => { load(); onReload?.(); }}/>
      <SchemaEditor schema={schema || []} onChange={setSchema} onSave={saveSchema} saving={saving} typeName={full.name}/>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
function RecipeEditor({ full, onReload }) {
  const [categories, setCategories] = useState([]);
  const [addMode, setAddMode] = useState(false);
  const [form, setForm] = useState({ match_mode: "category", product_category_id: null, product_category_name: "", product_code: "", product_name: "", qty_min: 1, qty_max: 1, qty_free: 1 });
  const [catQ, setCatQ] = useState("");

  useEffect(() => {
    api.get("/v1/odoo/product-categories", { params: { limit: 500 } })
      .then((r) => setCategories(r.data.items || []))
      .catch(() => {});
  }, []);

  const filteredCats = useMemo(() => {
    const t = catQ.trim().toLowerCase();
    if (!t) return categories.slice(0, 30);
    return categories.filter((c) => (c.name || "").toLowerCase().includes(t)).slice(0, 30);
  }, [catQ, categories]);

  const add = async () => {
    if (form.match_mode === "category" && !form.product_category_id) return toast.error("Elige una categoría");
    if (form.match_mode === "exact" && !form.product_code) return toast.error("Ingresa el SKU");
    try {
      await api.post(`/v1/mesa/solution-types/${full.id}/materials`, form);
      toast.success("Línea agregada");
      setAddMode(false);
      setForm({ match_mode: "category", product_category_id: null, product_category_name: "", product_code: "", product_name: "", qty_min: 1, qty_max: 1, qty_free: 1 });
      onReload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const del = async (mid) => {
    if (!confirm("¿Eliminar línea?")) return;
    try {
      await api.delete(`/v1/mesa/solution-types/materials/${mid}`);
      toast.success("Eliminada");
      onReload?.();
    } catch (e) { toast.error("Error"); }
  };

  const materials = full.own_materials || [];

  return (
    <div className="bg-white border rounded-lg">
      <div className="p-2 border-b bg-slate-50 flex items-center gap-2">
        <Package size={14} className="text-blue-600"/>
        <div className="flex-1">
          <div className="text-[13px] font-bold" style={{ color: "#0A3747" }}>Receta · {materials.length} líneas</div>
          <div className="text-[10px] text-gray-500">Mezcla producto exacto y categoría</div>
        </div>
        {!addMode && (
          <button onClick={() => setAddMode(true)} className="text-[10px] px-2 py-1 rounded bg-blue-600 text-white flex items-center gap-1" data-testid="btn-add-material">
            <Plus size={11}/> Añadir material
          </button>
        )}
      </div>

      {addMode && (
        <div className="p-3 border-b bg-blue-50 space-y-2">
          <div className="flex gap-2">
            <label className="text-[10px] flex items-center gap-1">
              <input type="radio" checked={form.match_mode === "category"} onChange={() => setForm({ ...form, match_mode: "category" })}/>
              Categoría (técnico elige SKU en campo)
            </label>
            <label className="text-[10px] flex items-center gap-1">
              <input type="radio" checked={form.match_mode === "exact"} onChange={() => setForm({ ...form, match_mode: "exact" })}/>
              Producto exacto (SKU fijo)
            </label>
          </div>
          {form.match_mode === "category" ? (
            <>
              {/* v3.5.1 · Selector jerárquico del árbol product.category de Odoo */}
              <CategoryTreePicker
                value={form.product_category_id}
                onChange={(cat) => setForm({
                  ...form,
                  product_category_id: cat ? cat.id : null,
                  product_category_name: cat ? (cat.complete_name || cat.name) : "",
                })}
                placeholder="Elige categoría del árbol de Odoo…"
              />
            </>
          ) : (
            <>
              <input value={form.product_code} onChange={(e) => setForm({ ...form, product_code: e.target.value })} placeholder="SKU / Código" className="w-full text-xs px-2 py-1 border rounded" data-testid="input-sku"/>
              <input value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} placeholder="Nombre producto" className="w-full text-xs px-2 py-1 border rounded"/>
            </>
          )}
          <div className="grid grid-cols-3 gap-2">
            <label className="text-[10px]">MIN <input type="number" value={form.qty_min} onChange={(e) => setForm({ ...form, qty_min: Number(e.target.value) })} className="w-full text-xs px-1 py-0.5 border rounded"/></label>
            <label className="text-[10px]">MAX <input type="number" value={form.qty_max} onChange={(e) => setForm({ ...form, qty_max: Number(e.target.value) })} className="w-full text-xs px-1 py-0.5 border rounded"/></label>
            <label className="text-[10px]">GRATIS <input type="number" value={form.qty_free} onChange={(e) => setForm({ ...form, qty_free: Number(e.target.value) })} className="w-full text-xs px-1 py-0.5 border rounded"/></label>
          </div>
          <div className="flex gap-2">
            <button onClick={add} className="text-[10px] px-3 py-1 bg-emerald-600 text-white rounded" data-testid="btn-save-material">Guardar</button>
            <button onClick={() => setAddMode(false)} className="text-[10px] px-3 py-1 bg-white border rounded">Cancelar</button>
          </div>
        </div>
      )}

      <div className="max-h-[55vh] overflow-y-auto">
        {materials.length === 0 && !addMode ? (
          <div className="p-6 text-center text-[11px] text-gray-400">Sin líneas. Añade una.</div>
        ) : (
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50">
              <tr>
                <th className="p-1 text-left">Modo</th>
                <th className="p-1 text-left">Producto / Categoría</th>
                <th className="p-1 text-right">MIN</th>
                <th className="p-1 text-right">MAX</th>
                <th className="p-1 text-right">GRATIS</th>
                <th className="p-1"></th>
              </tr>
            </thead>
            <tbody>
              {materials.map((m) => (
                <tr key={m.id} className="border-t">
                  <td className="p-1">
                    <span className={`px-1 py-0.5 text-[9px] rounded font-bold ${(m.match_mode || "exact") === "category" ? "bg-purple-100 text-purple-800" : "bg-blue-100 text-blue-800"}`}>
                      {(m.match_mode || "exact").toUpperCase()}
                    </span>
                  </td>
                  <td className="p-1">
                    {(m.match_mode === "category") ? (
                      <span>📁 {m.product_category_name || `Categoría #${m.product_category_id}`}</span>
                    ) : (
                      <span>🔖 {m.product_code} · <span className="text-gray-500">{m.product_name}</span></span>
                    )}
                  </td>
                  <td className="p-1 text-right font-mono">{m.qty_min}</td>
                  <td className="p-1 text-right font-mono">{m.qty_max}</td>
                  <td className="p-1 text-right font-mono">{m.qty_free}</td>
                  <td className="p-1"><button onClick={() => del(m.id)} className="text-red-500 hover:text-red-700" data-testid={`btn-del-material-${m.id}`}><Trash2 size={11}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
function SchemaEditor({ schema, onChange, onSave, saving, typeName }) {
  const addField = () => onChange([...schema, { key: `campo_${schema.length + 1}`, label: "Nuevo campo", type: "text", required: false }]);
  const del = (i) => onChange(schema.filter((_, idx) => idx !== i));
  const upd = (i, patch) => onChange(schema.map((f, idx) => idx === i ? { ...f, ...patch } : f));
  return (
    <div className="bg-white border rounded-lg">
      <div className="p-2 border-b bg-slate-50 flex items-center gap-2">
        <FolderTree size={14} className="text-purple-600"/>
        <div className="flex-1">
          <div className="text-[13px] font-bold" style={{ color: "#0A3747" }}>Schema Fiscalización · {schema.length} campos</div>
          <div className="text-[10px] text-gray-500">{typeName}</div>
        </div>
        <button onClick={addField} className="text-[10px] px-2 py-1 rounded bg-purple-600 text-white flex items-center gap-1" data-testid="btn-add-field"><Plus size={11}/> Campo</button>
        <button onClick={onSave} disabled={saving} className="text-[10px] px-2 py-1 rounded bg-emerald-600 text-white flex items-center gap-1" data-testid="btn-save-schema">
          {saving ? <Loader2 size={11} className="animate-spin"/> : <Save size={11}/>} Guardar
        </button>
      </div>
      <div className="max-h-[60vh] overflow-y-auto p-2 space-y-2">
        {schema.length === 0 ? (
          <div className="p-6 text-center text-[11px] text-gray-400">Sin campos. Añade uno.</div>
        ) : schema.map((f, i) => (
          <div key={i} className="border rounded p-2 bg-gray-50">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[9px] font-bold text-gray-500">#{i + 1}</span>
              <input value={f.key} onChange={(e) => upd(i, { key: e.target.value })} placeholder="key" className="flex-1 text-[10px] font-mono px-1 py-0.5 border rounded" data-testid={`schema-key-${i}`}/>
              <select value={f.type} onChange={(e) => upd(i, { type: e.target.value })} className="text-[10px] px-1 py-0.5 border rounded" data-testid={`schema-type-${i}`}>
                {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <label className="text-[10px] flex items-center gap-1">
                <input type="checkbox" checked={!!f.required} onChange={(e) => upd(i, { required: e.target.checked })}/> obligatorio
              </label>
              <button onClick={() => del(i)} className="text-red-500 hover:text-red-700" data-testid={`schema-del-${i}`}><Trash2 size={11}/></button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input value={f.label} onChange={(e) => upd(i, { label: e.target.value })} placeholder="label (etiqueta)" className="text-[10px] px-1 py-0.5 border rounded"/>
              <input value={f.unit || ""} onChange={(e) => upd(i, { unit: e.target.value })} placeholder="unidad (ej. dBm, m)" className="text-[10px] px-1 py-0.5 border rounded"/>
            </div>
            {f.type === "number" && (
              <div className="grid grid-cols-3 gap-2 mt-1">
                <input type="number" value={f.min ?? ""} onChange={(e) => upd(i, { min: e.target.value === "" ? undefined : Number(e.target.value) })} placeholder="min" className="text-[10px] px-1 py-0.5 border rounded"/>
                <input type="number" value={f.max ?? ""} onChange={(e) => upd(i, { max: e.target.value === "" ? undefined : Number(e.target.value) })} placeholder="max" className="text-[10px] px-1 py-0.5 border rounded"/>
                <input type="number" value={f.decimals ?? ""} onChange={(e) => upd(i, { decimals: e.target.value === "" ? undefined : Number(e.target.value) })} placeholder="decimales" className="text-[10px] px-1 py-0.5 border rounded"/>
              </div>
            )}
            {f.type === "select" && (
              <input
                value={(f.options || []).join(", ")}
                onChange={(e) => upd(i, { options: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
                placeholder="opciones separadas por coma"
                className="w-full text-[10px] px-1 py-0.5 border rounded mt-1"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
